import React, { useCallback, useState } from "react";
import { atomWithReset } from "jotai/utils";
import { useAtom, useSetAtom } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik, Form } from "formik";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import Button from "@mui/material/Button";
import { Close, ContentCopy, Save } from "@mui/icons-material";

import FormikTextInput from "../../../formikComponents/formik-text-input";
import { customAlertStateAtom } from "../../../components/custom-alert";
import apiClient from "../../../utils/api-client";
import { trackSentryException } from "../../../utils/helpers";

type CreatedKey = {
  id: string;
  name: string;
  displayPrefix: string;
  key: string;
  createdAt: string | null;
  createdBy: string | null;
  lastUsedAt: string | null;
};

const createApiKeyDialogStateAtom = atomWithReset<{ open: boolean }>({
  open: false,
});

const CreateApiKeyDialog = ({
  companyId,
}: {
  companyId: string | undefined;
}) => {
  const [dialogState, setDialogState] = useAtom(createApiKeyDialogStateAtom);
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();
  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const handleClose = useCallback(
    (_event: {}, reason?: string) => {
      if (
        reason &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      ) {
        return;
      }
      setDialogState({ open: false });
      setCreatedKey(null);
      setCopied(false);
    },
    [setDialogState]
  );

  const createMutation = useMutation({
    mutationKey: ["createApiKey"],
    mutationFn: async (name: string) => {
      const response = await apiClient.post<CreatedKey>(
        "/settings/api-keys",
        { name }
      );
      return response.data;
    },
    onSuccess: async (data) => {
      setCreatedKey(data);
      await queryClient.invalidateQueries({
        queryKey: ["getApiKeys", companyId],
      });
    },
    onError: (error: any) => {
      trackSentryException(error);
      setCustomAlertState({
        open: true,
        type: "error",
        title: "Failed to create API key",
        description:
          error?.response?.data?.detail ??
          (error instanceof Error ? error.message : String(error)),
      });
    },
  });

  const handleCopy = useCallback(async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      setCustomAlertState({
        open: true,
        type: "success",
        title: "Copied to clipboard",
      });
    } catch (error) {
      trackSentryException(error);
    }
  }, [createdKey, setCustomAlertState]);

  return (
    <Dialog
      maxWidth={"sm"}
      fullWidth={true}
      open={dialogState.open}
      onClose={handleClose}
    >
      {createdKey == null ? (
        <Formik
          initialValues={{ name: "" }}
          validate={(values) => {
            const errors: { name?: string } = {};
            if (!values.name.trim()) {
              errors.name = "Required";
            }
            return errors;
          }}
          onSubmit={async (values) => {
            await createMutation.mutateAsync(values.name.trim());
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <DialogTitle sx={{ paddingRight: "8px", paddingTop: "10px" }}>
                <Box display="flex" alignItems="top">
                  <Box flexGrow={1} paddingRight="6px" paddingTop="4px">
                    Create API Key
                  </Box>
                  <Box>
                    <Tooltip title="Close">
                      <span>
                        <IconButton
                          disabled={createMutation.isPending}
                          onClick={() =>
                            handleClose({}, "closeIconButtonClose")
                          }
                        >
                          <Close />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent dividers className="flex flex-col gap-3.5">
                <FormikTextInput
                  name="name"
                  label="Name"
                  placeholder="e.g. CI pipeline"
                  type="text"
                  required
                />
              </DialogContent>
              <DialogActions>
                <Button
                  loading={isSubmitting || createMutation.isPending}
                  type="submit"
                  loadingPosition="start"
                  startIcon={<Save />}
                  variant="contained"
                >
                  Create
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      ) : (
        <>
          <DialogTitle sx={{ paddingRight: "8px", paddingTop: "10px" }}>
            <Box display="flex" alignItems="top">
              <Box flexGrow={1} paddingRight="6px" paddingTop="4px">
                API Key Created
              </Box>
              <Box>
                <Tooltip title="Close">
                  <IconButton
                    onClick={() => handleClose({}, "closeIconButtonClose")}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers className="flex flex-col gap-3.5">
            <Alert severity="warning">
              This key will only be shown once. Copy it now and store it
              somewhere safe — you will not be able to retrieve it again.
            </Alert>
            <TextField
              fullWidth
              label="API Key"
              value={createdKey.key}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied!" : "Copy"}>
                      <IconButton onClick={handleCopy} edge="end">
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              onFocus={(e) => e.target.select()}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => handleClose({}, "closeIconButtonClose")}
            >
              Done
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CreateApiKeyDialog;
export { createApiKeyDialogStateAtom };
